import React, {ReactElement} from 'react';

import {
  CloneReportModal,
  DiscardReportDraftAnalyticsOrigin,
  DiscardReportDraftModal,
  EditReportTrigger,
  ReportExportModal,
  SetPreviewImageModal,
  StargazerModal,
} from '../../../components/ReportModals';
import history from '../../../util/history';
import * as Urls from '../../../util/urls';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {ReportPageAction} from './ReportPageMenuActions';

type ReportPageModalType =
  | ReportPageAction.EditUpdatedReport
  | ReportPageAction.SetPreviewImage
  | ReportPageAction.CloneDraft
  | ReportPageAction.MoveReport
  | ReportPageAction.DeleteDraft
  | ReportPageAction.DownloadPDF
  | ReportPageAction.ViewStars;

type ReportPageModalDetails = Record<ReportPageModalType, () => ReactElement>;

type ReportPageModalProps = {
  modalType?: ReportPageAction;
  setModalType: (modalType?: ReportPageAction) => void;
  setIsLoading: (isLoading: boolean) => void;
};

export const ReportPageModal = ({
  modalType,
  setModalType,
  setIsLoading,
}: ReportPageModalProps) => {
  const {entityName, projectName, isPublicProject} = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();

  const handleOnClose = () => {
    setModalType(undefined);
  };

  const modalDetails: ReportPageModalDetails = {
    [ReportPageAction.EditUpdatedReport]: () => (
      <EditReportTrigger
        view={view}
        entityName={entityName}
        projectName={projectName}
        onModalClose={handleOnClose}
        setIsLoading={setIsLoading}
      />
    ),
    [ReportPageAction.SetPreviewImage]: () => (
      <SetPreviewImageModal
        view={view}
        reportViewRef={reportViewRef}
        isPublicProject={isPublicProject}
        isModalOpen={true}
        handleOnClose={handleOnClose}
      />
    ),
    [ReportPageAction.CloneDraft]: () => (
      <CloneReportModal
        viewRef={reportViewRef}
        isPublicProject={isPublicProject}
        onClose={handleOnClose}
      />
    ),
    [ReportPageAction.MoveReport]: () => (
      <CloneReportModal
        viewRef={reportViewRef}
        moveReport={true}
        isPublicProject={isPublicProject}
        onClose={handleOnClose}
      />
    ),
    [ReportPageAction.DeleteDraft]: () => (
      <DiscardReportDraftModal
        isModalOpen={true}
        view={view}
        draftId={view.id ?? ''}
        analyticsOrigin={DiscardReportDraftAnalyticsOrigin.ReportPage}
        onDiscardClick={() => {
          projectName != null
            ? history.push(
                Urls.reportList({
                  entityName,
                  projectName,
                })
              )
            : history.push(Urls.reportGallery());
        }}
        onModalClose={handleOnClose}
      />
    ),
    [ReportPageAction.DownloadPDF]: () => (
      <ReportExportModal
        open={true}
        reportViewRef={reportViewRef}
        onClose={handleOnClose}
      />
    ),
    [ReportPageAction.ViewStars]: () => (
      <StargazerModal
        open={true}
        reportViewRef={reportViewRef}
        onClose={handleOnClose}
      />
    ),
  };

  if (modalType == null || !(modalType in modalDetails)) {
    return null;
  }

  return modalDetails[modalType as ReportPageModalType]();
};
